<template>
  <aside
    id="modal-gallery-project"
    ref="aside"
    class="modal modal--default micromodal-slide"
    aria-hidden="true"
    :modal-open="modalOpen"
  >
    <div class="modal__overlay" tabindex="-2" data-micromodal-close>
      <div class="resources-index__loading-container">
        <div v-if="loadingData" class="loading" />
      </div>
      <div
        v-if="!loadingData"
        class="gp__container modal__container"
        id="gp-container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-gallery-project-title"
      >
        <div class="gp__left outside-column" data-micromodal-close />
        <div class="gp__body grid" id="projectBody">
          <div class="gp__hero-image w-full bg-white">
            <div
              class="gp__hero-image"
              :style="{
                backgroundImage:
                  'url(https://img.imageboss.me/width/' +
                  heroImageWidth +
                  '/webp:auto/' +
                  project.defaultThumbnailImageUrl +
                  ')',
              }"
            />
          </div>

          <div class="lg:grid-wrapper relative">
            <div class="grid-row">
              <div
                class="col-10 col-offset-1 md:col-7 md:col-offset-4 lg:col-6 lg:col-offset-5 relative"
              >
                <div class="gp__hero-info">
                  <div class="industry" v-if="!!project.industry">
                    <p
                      class="label-normal font-light label--line leading-normal mb-6"
                    >
                      {{ project.industry | capitalizeAll }}
                    </p>
                  </div>

                  <h1 class="h5 mb-4 font-medium">
                    {{ project.title }}
                  </h1>

                  <div class="designer" v-if="!!project.designFirm">
                    <p class="label-normal font-light leading-normal">
                      Design by
                    </p>
                    <p class="leading-normal font-medium mb-4">
                      {{ project.designFirm }}
                    </p>
                  </div>

                  <div class="location" v-if="!!project.location">
                    <p class="label-normal font-light leading-normal">
                      Location
                    </p>
                    <p class="leading-normal font-medium mb-4">
                      {{ project.location }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p
            class="gp__description col-10 lg:col-8 col-offset-1 lg:col-offset-2"
            v-html="project.description"
          ></p>

          <div v-if="project.projectDetails.length > 0">
            <div class="gp__details-wrapper">
              <div class="title h5 pr-4 font-medium md:col-3 md:text-right">
                Project Details
              </div>
              <div class="attributes md:col-9 grid-row m-0">
                <div
                  class="attr col-6 md:col-3 mb-6"
                  v-for="(attr, attrIndex) in project.projectDetails"
                  :key="`${attr[0]}-${attrIndex}}`"
                >
                  <div
                    class="category border-b-1 border-grey-light font-light label-normal mb-4 pb-2"
                    v-html="attr[0]"
                  />
                  <div
                    class="gp__properties leading-normal font-medium mb-3"
                    v-for="(property, propIndex) in attr[1]"
                    :key="`${property.contentName}-${propIndex}`"
                  >
                    <a
                      v-if="!!property.contentUrl"
                      :href="`${property.contentUrl}`"
                      @click="sendAdobeLinkNavClick($event, property)"
                    >
                      {{ property.contentName }}
                      <strong class="font-medium">&nbsp;&rsaquo;</strong>
                    </a>
                    <p v-if="!property.contentUrl">
                      {{ property.contentName }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="showCaseStudy" class="gp__case-study-wrapper">
            <div
              class="case-study-media"
              :style="{
                backgroundImage:
                  'url( ' + project.defaultThumbnailImageUrl + ')',
              }"
            ></div>
            <div class="case-study-info grid-wrapper mx-10 mt-10">
              <div class="gp__help-text label--line mb-6 col-offset-1">
                Case Study
              </div>
              <div class="case-study-info grid-row">
                <span class="left col-3 col-offset-1">
                  <h3 class="headline h5 font-medium">
                    case study headline
                  </h3>
                </span>
                <span class="right col-7">
                  <p
                    class="case-study-description label-normal"
                    v-html="project.caseStudyIntroduction"
                  ></p>
                  <span role="button" class="link-cta my-8 w-fit">
                    <svg
                      class="icon icon-cta icon--start align-bottom"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <use xlink:href="#icon-cta" />
                    </svg>
                    <span class="a a--letter-spacing-sm"
                      >Read the Case Study</span
                    >
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div class="gp__images-wrapper container">
            <div
              class="image-wrapper"
              v-for="(image, index) in project.images"
              :class="{ smaller: index > 1 }"
              :key="`${image.id}`"
            >
              <div v-if="!!image.isVideo" class="vimeo-wrapper">
                <vimeo-video :videoid="image.videoId" fitaspect></vimeo-video>
                <div class="flex md:justify-between p-2">
                  <span class="font-light label-normal">{{
                    image.caption
                  }}</span>
                </div>
              </div>
              <div v-if="!image.isVideo">
                <gallery-project-image
                  :image="image"
                  :showHotspots="showHotspots"
                  :imageIndex="index"
                ></gallery-project-image>
                <div class="flex md:justify-between my-4">
                  <span class="font-light label-normal">{{
                    image.caption
                  }}</span>
                  <a
                    :href="`${image.url}`"
                    download
                    target="_blank"
                    class="link-download-image"
                    @click="sendAdobeDownloadClick(image, index)"
                  >
                    <svg
                      class="icon icon-download icon--start"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <use xlink:href="#icon-download"></use>
                    </svg>
                    Download image
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div
            class="gp__similar-projects-wrapper"
            v-if="
              !!project.similarProjects && project.similarProjects.length > 0
            "
          >
            <div class="col-12">
              <p class="body mt-6 title col-3 p-6 font-medium h1">
                Similar Projects
              </p>
            </div>
            <div class="col-12">
              <div class="swiper swiper-3up overflow-hidden w-borderfix">
                <div
                  class="vue-swiper-3up swiper-container swiper-3up__container"
                >
                  <div class="swiper-wrapper swiper-3up__wrapper">
                    <div
                      v-for="similarProject in project.similarProjects"
                      v-bind:key="similarProject.slug"
                      class="swiper-slide swiper-3up__slide"
                    >
                      <div
                        class="card-driver js-accessible-card  card-driver--border card-driver--border-padding px-2 md:px-4 "
                      >
                        <div class="card-driver__image-outer image-loading">
                          <img
                            class="card-driver__image"
                            :alt="similarProject.name"
                            :src="
                              `https://img.imageboss.me/width/1024/webp:auto/${similarProject.defaultThumbnailImageUrl}`
                            "
                            :srcset="
                              `https://img.imageboss.me/width/1024/webp:auto/${similarProject.defaultThumbnailImageUrl} 1024w,
                                https://img.imageboss.me/width/800/webp:auto/${similarProject.defaultThumbnailImageUrl} 800w,
                                https://img.imageboss.me/width/640/webp:auto/${similarProject.defaultThumbnailImageUrl} 640w,
                                https://img.imageboss.me/width/480/webp:auto/${similarProject.defaultThumbnailImageUrl} 480w,
                                https://img.imageboss.me/width/320/webp:auto/${similarProject.defaultThumbnailImageUrl} 320w`
                            "
                            sizes="(min-width: 85.5em) 652px, (min-width: 48em) 50vw, 100vw"
                          />
                        </div>
                        <div
                          class="card-driver__content-outer card-driver__content-outer--right "
                        >
                          <div class="card-driver__content ">
                            <a
                              class="no-underline js-accessible-link"
                              :href="similarProjectUrl(similarProject.id)"
                            >
                              <div class="card-driver__content-inner">
                                <div
                                  class="industry"
                                  v-if="!!similarProject.industry"
                                >
                                  <p
                                    class="label-normal font-light label--line leading-normal mb-6"
                                  >
                                    {{
                                      similarProject.industry | capitalizeAll
                                    }}
                                  </p>
                                </div>
                                <h3 class="">
                                  <span class="card-driver__title block">{{
                                    similarProject.name
                                  }}</span>
                                </h3>
                              </div>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  class="js-swiper-3up-pagination swiper-pagination swiper__pagination swiper-3up__pagination"
                />
                <div class="swiper__arrows swiper-3up__arrows">
                  <div
                    class="js-swiper-3up-button-prev swiper-button-prev swiper__button swiper__button--prev"
                  >
                    <svg
                      class="icon icon-arrow-pagination icon-arrow-pagination--rotate"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <use xlink:href="#icon-arrow-pagination" /></svg
                    ><span class="sr-only">Previous slide</span>
                  </div>
                  <div
                    class="js-swiper-3up-button-next swiper-button-next swiper__button swiper__button--next"
                  >
                    <svg
                      class="icon icon-arrow-pagination"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <use xlink:href="#icon-arrow-pagination" /></svg
                    ><span class="sr-only">Next slide</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!--End gp__body-->

        <div class="gp__right outside-column" data-micromodal-close>
          <!-- Close button -->
          <div ref="handleClose" class="gp__close" data-micromodal-close>
            <svg class="icon icon-close" aria-hidden="true" focusable="false">
              <use xlink:href="#icon-close" />
            </svg>
          </div>

          <!-- Social buttons -->
          <ul class="gp__socials flex-col">
            <li
              class="gp__list-item"
              :data-href="facebookDataHref()"
              data-layout="button_count"
              data-size="small"
            >
              <a
                tabindex="1"
                target="_blank"
                :href="facebookShareHref()"
                class="fb-xfbml-parse-ignore"
                @click="sendAnalyticsShareEvent('facebook')"
              >
                <svg class="icon icon-facebook">
                  <use xlink:href="#icon-facebook"></use>
                </svg>
                <span class="sr-only">Facebook</span>
              </a>
            </li>
            <li class="gp__list-item">
              <a
                tabindex="1"
                :href="linkedInShareHref()"
                target="_blank"
                rel="noopener"
                @click="sendAnalyticsShareEvent('linkedin')"
              >
                <svg class="icon icon-linkedin">
                  <use xlink:href="#icon-linkedin"></use>
                </svg>
                <span class="sr-only">LinkedIn</span>
              </a>
            </li>
            <li class="gp__list-item">
              <a
                :href="pinterestShareHref()"
                target="_blank"
                rel="noopener"
                @click="sendAnalyticsShareEvent('pinterest')"
              >
                <svg class="icon icon-pinterest">
                  <use xlink:href="#icon-pinterest"></use>
                </svg>
                <span class="sr-only">Pinterest</span>
              </a>
            </li>
          </ul>

          <!-- Next buttons -->
        </div>
      </div>
      <!--End container-->
    </div>
  </aside>
</template>

<script>
import debounce from 'lodash/debounce';
import threeUp from '../../../components/carousel-driver-3up';
import axios from 'axios';
import GalleryProjectImage from '../GalleryProjectImage';
import {
  eventBus,
  GALLERY_PROJECT_IMAGE_LOADED,
  CLOSE_GALLERY_PROJECT_MODAL,
} from '../../shared/event-bus';
import {
  handleAdobeNavEvent,
  analyticsLabelFromString,
  analyticsCtaDataObject,
  handleFileDownloadEvent,
} from '../../../helpers/adobe-analytics';
import { VimeoVideo } from '../../../helpers/vimeo-video.js';

export default {
  name: 'GalleryProjectModal',
  components: {
    GalleryProjectImage,
  },
  props: [
    'galleryProjectModalId',
    'link',
    'modalOpen',
    'projectId',
    'searchTerm',
    'tags',
  ],
  data() {
    return {
      loadingData: true,
      projectLoaded: false,
      projectData: {},
      projectImagesLoaded: 0,
    };
  },
  mounted() {
    eventBus.$on(CLOSE_GALLERY_PROJECT_MODAL, () => {
      this.handleClose();
    });

    // Set height of outside columns to make the buttons sticky
    eventBus.$on(GALLERY_PROJECT_IMAGE_LOADED, data => {
      if (data) {
        this.projectImagesLoaded++;
        // Make sure all images are loaded before setting the height
        if (
          this.project.images &&
          this.projectImagesLoaded === this.project.images.length
        ) {
          // Wait for all images to be rendered so height is accurate
          this.$nextTick(_ => {
            const bodyHeight = document.getElementById('projectBody')
              .offsetHeight;
            const outsideColumns =
              document.getElementsByClassName('outside-column') || [];
            Array.from(outsideColumns).forEach(column => {
              column.style.height = `${bodyHeight}px`;
            });
          });
        }
      }
    });
  },
  computed: {
    metaTags() {
      const transformedTags = this.tags
        ? this.tags.split(',').reduce((acc, currVal) => {
            if (currVal === '') return acc;
            let newAcc = [...acc, currVal.trim()];
            return newAcc;
          }, [])
        : [];
      if (transformedTags.length > 0) {
        return { name: 'tags', value: transformedTags };
      }
      return null;
    },
    project() {
      const { projectData } = this;
      if (!projectData) {
        return;
      }

      const thumbnailImg = !!projectData.defaultThumbnailImageUrl
        ? projectData.defaultThumbnailImageUrl
        : !!projectData.images
        ? projectData.images[0].url
        : '';

      const images = projectData.images || [];
      if (projectData.videoUrl) {
        const videoId = projectData.videoUrl.match(/vimeo\.com\/(\d+)/)[1];
        images.unshift({
          caption: projectData.videoCaption,
          isVideo: true,
          videoId,
        });
      }

      const project = {
        images,
        description: projectData.description,
        designFirm: projectData.designFirm,
        industry: projectData.industry,
        title: projectData.name,
        location: this.formattedLocation(projectData),
        caseStudyIntroduction: projectData.caseStudyIntroduction,
        caseStudyId: projectData.caseStudyId,
        projectDetails: this.formattedProjectDetails(projectData),
        similarProjects: projectData.similarProjects,
        defaultThumbnailImageUrl: thumbnailImg,
      };

      return project;
    },
    showCaseStudy() {
      return !!this.project && !!this.project.caseStudyId;
    },
    showHotspots() {
      return window.innerWidth > 768;
    },
    heroImageData() {
      return this.project.images ? this.projectData.images[0] : '';
    },
    heroImageWidth() {
      if (window.matchMedia('(max-width: 599px)').matches) return 640;
      if (
        window.matchMedia('(min-width: 600px) and (max-width: 1024px)').matches
      )
        return 1024;
      return 1600;
    },
  },
  updated: debounce(function() {
    if (this.modalOpen) {
      threeUp.init('.vue-swiper-3up');
      window.initAccessibleCards();
      if (!this.projectLoaded) {
        this.getProject();
      }
    }
  }, 250),
  methods: {
    getProject() {
      this.loadingData = true;
      return axios
        .get(`/gallery_projects/${this.projectId}.json?`)
        .then(({ data }) => {
          this.projectData = data;
          this.loadingData = false;
          this.projectLoaded = true;
        });
    },
    similarProjectUrl(id) {
      return `${window.location.origin}/gallery/${id}`;
    },
    linkedInShareHref() {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${window.location.href}`;
    },
    facebookShareHref() {
      const facebookPartialUrl =
        'https://www.facebook.com/sharer/sharer.php?u=https%3A%2F%2F';
      const linkedlUrl = `www.3-form.com%2Fgallery%2F${this.projectId}&amp;src=sdkpreparse"`;

      return `${facebookPartialUrl}${linkedlUrl}`;
    },
    facebookDataHref() {
      return `https://www.3-form.com/gallery/${this.projectId}`;
    },
    pinterestShareHref() {
      return `https://www.pinterest.com/pin-creation-tool/`;
    },
    handleClose(_modal) {
      this.projectData = {};
      this.loadingData = false;
      this.projectLoaded = false;
      this.projectImagesLoaded = 0;

      // analytics cleanup
      const siteObj = window.analytics?.site || {};
      delete siteObj.sitesection2;
      delete siteObj.sitesection3;
      delete siteObj.sitesection4;

      eventBus.updateSelectedGalleryProject();
    },
    formattedLocation(projectData) {
      const location = [];
      const locationData = [
        projectData.city,
        projectData.state,
        projectData.country,
      ];
      locationData.forEach(data => {
        if (!!data) {
          location.push(data);
        }
      });
      return location.join(', ');
    },
    formattedProjectDetails(projectData) {
      const projectDetails = projectData.projectDetails;
      const projectAttrs = {};
      if (!projectData.projectDetailCategories) {
        return [];
      }
      projectData.projectDetailCategories.forEach(category => {
        projectAttrs[category] = [];
      });
      if (projectDetails) {
        projectDetails.forEach(detail => {
          projectAttrs[detail.category].push({
            contentName: detail.contentName,
            contentUrl: detail.contentUrl,
          });
        });
      }
      const categoriesWithValues = Object.entries(projectAttrs).filter(
        x => x[1].length > 0
      );
      return categoriesWithValues;
    },
    sendAdobeLinkNavClick(event, property) {
      const navString = `NAV|Gallery Project Details|${property.contentName}`;
      handleAdobeNavEvent({
        navString,
        destinationUrl: property.contentUrl,
        event,
      });
    },
    sendAdobeDownloadClick(image, index) {
      const filename = image.caption || `${image.id} Image ${index + 1}`;
      const file_name = analyticsLabelFromString(filename);
      const downloadData = {
        file_format: 'JPG',
        file_name,
        link_url: image.url,
        link_type: 'IMAGE',
        placement: analyticsLabelFromString(`${this.project.title}`),
      };

      handleFileDownloadEvent(analyticsCtaDataObject(downloadData));
    },
    sendAnalyticsShareEvent(platform) {
      // Google Analytics
      if (typeof gtag === 'function') {
        gtag('event', platform, {
          event_category: 'Gallery Social Share',
          event_label: this.projectId,
        });
      }

      // Adobe Analytics
      const navString = `EXIT|Gallery Project Share|${platform}`;
      const shareHrefs = {
        facebook: this.facebookShareHref,
        linkedin: this.linkedInShareHref,
        pinterest: this.pinterestShareHref,
      };
      const destinationUrl = shareHrefs[platform].call(this);
      handleAdobeNavEvent({
        navString,
        destinationUrl,
        target_blank: true,
        optional_rel: 'noopener',
      });
    },
  },
};
</script>
